/**
 * View Transition Navigation Direction Detection
 *
 * Detects whether navigation is forward (new page) or back (browser back button)
 * to apply appropriate view transition animations.
 *
 * Usage: Import this module in pages that need directional view transitions.
 * The script will automatically set data-navigation="forward|back" on the <html> element.
 */

(function () {
    'use strict';

    // Initialize navigation tracking
    const STORAGE_KEY = 'wesog-nav-state';
    let isBackNavigation = false;

    /**
     * Get or initialize navigation state from sessionStorage
     * @returns {Object} Navigation state object
     */
    function getNavigationState() {
        try {
            const stored = sessionStorage.getItem(STORAGE_KEY);
            return stored
                ? JSON.parse(stored)
                : { historyLength: 0, lastAction: 'forward' };
        } catch (e) {
            return { historyLength: 0, lastAction: 'forward' };
        }
    }

    /**
     * Save navigation state to sessionStorage
     * @param {Object} state - Navigation state to save
     */
    function setNavigationState(state) {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (e) {
            // Storage might be disabled
        }
    }

    /**
     * Detect navigation direction using multiple methods
     * @returns {string} 'back' or 'forward'
     */
    function detectNavigationDirection() {
        const navState = getNavigationState();
        const currentHistoryLength = history.length;

        // Method 1: Detect back navigation by comparing history lengths
        if (navState.historyLength > 0 && currentHistoryLength < navState.historyLength) {
            isBackNavigation = true;
        } else if (navState.lastAction === 'back') {
            isBackNavigation = true;
        }

        // Method 2: Performance API check (modern browsers)
        if (window.performance && window.performance.getEntriesByType) {
            const navEntries = window.performance.getEntriesByType('navigation');
            if (navEntries.length > 0 && navEntries[0].type === 'back_forward') {
                isBackNavigation = true;
            }
        }

        // Method 3: Legacy Performance API check
        if (
            window.performance &&
            window.performance.navigation &&
            window.performance.navigation.type === 2
        ) {
            isBackNavigation = true;
        }

        return isBackNavigation ? 'back' : 'forward';
    }

    /**
     * Initialize navigation direction detection
     */
    function initializeNavigationDetection() {
        const navState = getNavigationState();
        const currentHistoryLength = history.length;

        // Check if this page load was triggered by a browser back/forward action
        let isBackNavigation = false;
        // let detectionMethod = 'default-forward';

        // Method 1: Performance API check (most reliable for browser back/forward)
        if (window.performance && window.performance.getEntriesByType) {
            const navEntries = window.performance.getEntriesByType('navigation');
            if (navEntries.length > 0 && navEntries[0].type === 'back_forward') {
                isBackNavigation = true;
                // detectionMethod = 'performance-api-modern';
            }
        }

        // Method 2: Legacy Performance API check
        if (
            !isBackNavigation &&
            window.performance &&
            window.performance.navigation &&
            window.performance.navigation.type === 2
        ) {
            isBackNavigation = true;
            // detectionMethod = 'performance-api-legacy';
        }

        // Method 3: History length comparison - but be smarter about it
        if (!isBackNavigation && navState.historyLength > 0) {
            // Only trust history length if we have a meaningful comparison
            // Fresh sessions often start with history.length = 1
            if (currentHistoryLength < navState.historyLength) {
                isBackNavigation = true;
                // detectionMethod = 'history-length-decrease';
            }
            // Handle case where history length stays the same but we know it was back
            else if (
                navState.lastAction === 'back' &&
                currentHistoryLength === navState.historyLength
            ) {
                // Only trust this if the performance API doesn't contradict it
                const perfEntry =
                    window.performance?.getEntriesByType?.('navigation')?.[0];
                const isDefinitelyForward =
                    perfEntry &&
                    (perfEntry.type === 'navigate' || perfEntry.type === 'reload');

                if (!isDefinitelyForward) {
                    isBackNavigation = true;
                    // detectionMethod = 'stored-back-action';
                }
            }
        }

        // Method 4: Fresh session handling - don't trust stored state for brand new sessions
        if (
            !isBackNavigation &&
            navState.historyLength === 0 &&
            currentHistoryLength === 1
        ) {
            // This looks like a fresh session or first page load
            // Only use Performance API, ignore stored state entirely
            const perfEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
            if (perfEntry && perfEntry.type === 'back_forward') {
                isBackNavigation = true;
                // detectionMethod = 'fresh-session-performance-api';
            } else {
                // Definitely a fresh load - default to forward
                isBackNavigation = false;
                // detectionMethod = 'fresh-session-default-forward';
            }
        }

        const navigationDirection = isBackNavigation ? 'back' : 'forward';

        // Set the navigation direction attribute
        document.documentElement.setAttribute('data-navigation', navigationDirection);

        // Debug logging
        // console.log('Navigation direction detected:', navigationDirection, {
        //     currentHistoryLength,
        //     storedHistoryLength: navState.historyLength,
        //     lastAction: navState.lastAction,
        //     performanceType:
        //         window.performance?.getEntriesByType?.('navigation')?.[0]?.type,
        //     detectedBy: detectionMethod,
        //     isFreshSession: navState.historyLength === 0 && currentHistoryLength === 1,
        // });

        // Update navigation state for next navigation
        setNavigationState({
            historyLength: currentHistoryLength,
            lastAction: navigationDirection,
        });
    }

    /**
     * Set up event listeners for navigation tracking
     */
    function setupNavigationListeners() {
        // Listen for popstate events (back/forward buttons)
        window.addEventListener('popstate', function () {
            // Set back navigation immediately and multiple times to ensure it sticks
            document.documentElement.setAttribute('data-navigation', 'back');

            // Force the attribute again after a short delay to handle any race conditions
            setTimeout(function () {
                document.documentElement.setAttribute('data-navigation', 'back');
            }, 0);

            setNavigationState({
                historyLength: history.length,
                lastAction: 'back',
            });
        });

        // Track forward navigation when clicking links - be more specific
        document.addEventListener('click', function (e) {
            const link = e.target.closest('a');
            if (
                link &&
                link.href &&
                !link.href.startsWith('javascript:') &&
                !link.href.startsWith('#') &&
                !link.href.startsWith('mailto:') &&
                !link.href.startsWith('tel:') &&
                link.target !== '_blank' // Don't track external links
            ) {
                // Set forward navigation immediately before the navigation happens
                document.documentElement.setAttribute('data-navigation', 'forward');

                // Update stored state
                setNavigationState({
                    historyLength: Math.max(history.length, 1),
                    lastAction: 'forward',
                });

                // Ensure it stays set during the navigation
                setTimeout(function () {
                    document.documentElement.setAttribute('data-navigation', 'forward');
                }, 0);
            }
        });

        // Override history methods to track programmatic navigation
        const originalPushState = history.pushState;
        const originalReplaceState = history.replaceState;

        history.pushState = function () {
            setNavigationState({
                historyLength: Math.max(history.length + 1, 1),
                lastAction: 'forward',
            });
            return originalPushState.apply(history, arguments);
        };

        history.replaceState = function () {
            setNavigationState({
                historyLength: Math.max(history.length, 1),
                lastAction: 'forward', // replaceState is typically forward navigation
            });
            return originalReplaceState.apply(history, arguments);
        };

        // Handle page visibility changes (when user comes back to tab)
        document.addEventListener('visibilitychange', function () {
            if (!document.hidden) {
                // Re-check navigation state when tab becomes visible
                setTimeout(function () {
                    const currentState = getNavigationState();
                    if (currentState.lastAction === 'back') {
                        document.documentElement.setAttribute('data-navigation', 'back');
                    }
                }, 10);
            }
        });

        // Add beforeunload listener to ensure we save state properly
        window.addEventListener('beforeunload', function () {
            const currentState = getNavigationState();
            const currentNavDirection =
                document.documentElement.getAttribute('data-navigation') || 'forward';

            // Ensure we don't lose the current state
            setNavigationState({
                historyLength: Math.max(history.length, currentState.historyLength, 1),
                lastAction: currentNavDirection,
            });
        });

        // Add a MutationObserver to protect the data-navigation attribute
        if (typeof MutationObserver !== 'undefined') {
            const attributeProtector = new MutationObserver(function (mutations) {
                mutations.forEach(function (mutation) {
                    if (
                        mutation.type === 'attributes' &&
                        mutation.attributeName === 'data-navigation' &&
                        mutation.target === document.documentElement
                    ) {
                        const currentValue =
                            document.documentElement.getAttribute('data-navigation');
                        if (
                            !currentValue ||
                            (currentValue !== 'forward' && currentValue !== 'back')
                        ) {
                            // Attribute was removed or set to invalid value, restore it
                            const lastKnownState = getNavigationState();
                            const fallbackDirection =
                                lastKnownState.lastAction || 'forward';
                            document.documentElement.setAttribute(
                                'data-navigation',
                                fallbackDirection,
                            );
                        }
                    }
                });
            });

            attributeProtector.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['data-navigation'],
            });
        }

        // Add a more aggressive check using pageshow event
        window.addEventListener('pageshow', function (e) {
            // Re-run navigation detection for back/forward cache scenarios
            if (e.persisted) {
                // This page was restored from bfcache (back/forward navigation)
                document.documentElement.setAttribute('data-navigation', 'back');
                setNavigationState({
                    historyLength: history.length,
                    lastAction: 'back',
                });
            }
        });
    }

    // CRITICAL: Set navigation attribute IMMEDIATELY to prevent race conditions
    // This runs synchronously before any view transitions can start
    (function setImmediateNavigationDirection() {
        const navState = getNavigationState();
        const currentHistoryLength = history.length;
        let isBackNavigation = false;
        // let quickDetectionMethod = 'quick-default-forward';

        // Quick Performance API check (most reliable)
        if (window.performance && window.performance.getEntriesByType) {
            const navEntries = window.performance.getEntriesByType('navigation');
            if (navEntries.length > 0 && navEntries[0].type === 'back_forward') {
                isBackNavigation = true;
                // quickDetectionMethod = 'quick-performance-api-modern';
            }
        }

        // Quick legacy Performance API check
        if (
            !isBackNavigation &&
            window.performance &&
            window.performance.navigation &&
            window.performance.navigation.type === 2
        ) {
            isBackNavigation = true;
            // quickDetectionMethod = 'quick-performance-api-legacy';
        }

        // For fresh sessions, force forward
        if (
            !isBackNavigation &&
            navState.historyLength === 0 &&
            currentHistoryLength === 1
        ) {
            isBackNavigation = false;
            // quickDetectionMethod = 'quick-fresh-session-forward';
        }

        const quickDirection = isBackNavigation ? 'back' : 'forward';

        // Set attribute immediately - this MUST happen synchronously
        document.documentElement.setAttribute('data-navigation', quickDirection);

        // console.log('Quick navigation direction set:', quickDirection, {
        //     method: quickDetectionMethod,
        //     timing: 'immediate-synchronous',
        // });
    })();

    // Then run the full detection after the critical timing window
    initializeNavigationDetection();

    // Set up listeners when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            setupNavigationListeners();
        });
    } else {
        setupNavigationListeners();
    }

    // Export for potential future use
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = {
            detectNavigationDirection,
            getNavigationState,
            setNavigationState,
        };
    }

    // Add to global scope for debugging
    window.wesogNavigation = {
        getState: getNavigationState,
        detect: detectNavigationDirection,
    };
})();
